import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

const services = [
  {
    icon: 'ri-paint-brush-line',
    title: 'Parking Lot Striping',
    description: 'Professional line striping for parking spaces, fire lanes, ADA compliance, and traffic flow markings.',
    link: '/services#striping'
  },
  {
    icon: 'ri-drop-line',
    title: 'Asphalt Sealcoating',
    description: 'Protect your pavement investment with high-quality sealcoating that extends asphalt life and enhances appearance.',
    link: '/services#sealcoating'
  },
  {
    icon: 'ri-tools-line',
    title: 'Crack Sealing & Repair',
    description: 'Prevent water damage and deterioration with professional crack sealing and pothole repair services.',
    link: '/services#repair'
  }
];

export default function ServiceGrid() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-16 lg:py-24 bg-white">
      <div className="max-w-[1180px] mx-auto px-6 lg:px-8">
        <div className={`mb-12 lg:mb-16 ${isVisible ? 'lg:animate-fade-up' : 'lg:opacity-0'}`}>
          <p className="text-yellow-500 uppercase tracking-wider text-sm font-semibold mb-3">
            Our Services
          </p>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-[1.05]" style={{ fontFamily: 'Oswald, sans-serif' }}>
            Complete Parking Lot Solutions
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className={`bg-gray-50 rounded-2xl p-6 lg:p-7 hover:shadow-xl transition-all duration-300 cursor-pointer group lg:card-hover ${
                isVisible ? 'lg:animate-fade-up' : 'lg:opacity-0'
              }`}
              style={{ animationDelay: `${80 * index}ms` }}
            >
              <div className="w-16 h-16 bg-yellow-400 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className={`${service.icon} text-3xl text-black`}></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {service.title}
              </h3>
              <p className="text-gray-600 leading-[1.45] mb-6">
                {service.description}
              </p>
              <Link
                to={service.link}
                className="inline-flex items-center text-yellow-500 font-semibold hover:text-yellow-600 transition-colors cursor-pointer"
              >
                Learn More
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
